import ajax from "./ajax";

/**
 * Seller onboarding (application before production).
 * Contract: SELLER_ONBOARDING_CONTRACT.md in the Node repo docs.
 * Every call resolves to the response body (not the axios response).
 */
const body = (promise) => promise.then((res) => res?.data ?? res);

// ── Seller ──────────────────────────────────────────────────────────────

export const getMyOnboarding = () => body(ajax("/seller-onboarding/me"));

export const saveOnboardingStep = (stepId, payload) =>
  body(
    ajax(`/seller-onboarding/me/steps/${encodeURIComponent(stepId)}`, {
      method: "PUT",
      data: payload,
    })
  );

export const sendOnboardingEmailVerification = () =>
  body(ajax("/seller-onboarding/me/email/send", { method: "POST" }));

export const refreshOnboardingEmail = () =>
  body(ajax("/seller-onboarding/me/email/refresh", { method: "POST" }));

// Completes the emailed verification link (/apply?mode=verifyEmail&oobCode=…).
export const verifyOnboardingEmail = (oobCode) =>
  body(
    ajax("/seller-onboarding/me/email/verify", {
      method: "POST",
      data: { oobCode },
    })
  );

export const startIdentityVerification = () =>
  body(ajax("/seller-onboarding/me/identity/session", { method: "POST" }));

export const refreshIdentityVerification = () =>
  body(ajax("/seller-onboarding/me/identity/refresh", { method: "POST" }));

export const uploadOnboardingDocument = (file, type, onUploadProgress) => {
  const form = new FormData();
  form.append("type", type);
  form.append("file", file);
  return body(
    ajax("/seller-onboarding/me/documents", {
      method: "POST",
      data: form,
      timeout: 120000,
      onUploadProgress,
    })
  );
};

export const deleteOnboardingDocument = (documentId) =>
  body(
    ajax(
      `/seller-onboarding/me/documents/${encodeURIComponent(documentId)}`,
      { method: "DELETE" }
    )
  );

export const confirmOnboardingPayment = (acknowledgements = []) =>
  body(
    ajax("/seller-onboarding/me/payment/confirm", {
      method: "POST",
      data: { acknowledgements },
    })
  );

export const acceptOnboardingAgreements = ({ accepted, typedName }) =>
  body(
    ajax("/seller-onboarding/me/agreements", {
      method: "POST",
      data: { accepted, typedName },
    })
  );

export const submitOnboardingApplication = () =>
  body(ajax("/seller-onboarding/me/submit", { method: "POST" }));

// Card saving reuses the existing billing endpoints (python service).
export const getCardSetup = () =>
  body(ajax("/invoices/payment_methods", { server: "python" }));

export const saveCardAsDefault = (paymentMethodId) =>
  body(
    ajax("/invoices/payment_methods", {
      method: "POST",
      server: "python",
      data: { paymentMethod: { id: paymentMethodId } },
    })
  );

// ── Admin ───────────────────────────────────────────────────────────────

export const getOnboardingConfig = () =>
  body(ajax("/seller-onboarding/config"));

export const saveOnboardingConfig = (config) =>
  body(
    ajax("/seller-onboarding/config", { method: "PUT", data: { config } })
  );

export const getOnboardingTemplate = () =>
  body(ajax("/seller-onboarding/template"));

export const getOnboardingReadiness = () =>
  body(ajax("/seller-onboarding/readiness"));

export const listSellerApplications = ({ status, limit } = {}) =>
  body(
    ajax("/seller-onboarding/applications", {
      query: {
        ...(status ? { status } : {}),
        ...(limit ? { limit } : {}),
      },
    })
  );

export const getSellerApplication = (uid) =>
  body(ajax(`/seller-onboarding/applications/${encodeURIComponent(uid)}`));

export const decideSellerApplication = (uid, { action, note, stepIds, approveResale }) =>
  body(
    ajax(
      `/seller-onboarding/applications/${encodeURIComponent(uid)}/decision`,
      { method: "POST", data: { action, note, stepIds, approveResale } }
    )
  );

export const markIdentityVerifiedManually = (uid, note) =>
  body(
    ajax(
      `/seller-onboarding/applications/${encodeURIComponent(uid)}/identity/manual`,
      { method: "POST", data: { note } }
    )
  );

export const setSellerOnboardingExempt = (uid, exempt) =>
  body(
    ajax(
      `/seller-onboarding/applications/${encodeURIComponent(uid)}/exempt`,
      { method: "POST", data: { exempt } }
    )
  );
